import { Link as Base } from "@corca/tiptap";
import { Plugin, PluginKey } from "@tiptap/pm/state";

import { getUrlOrigin } from "@/urls";

import { hasClickModifier } from "./utils/hasClickModifier";

import type { DocumentModel } from "@/models/DocumentModel";
import type { LinkOptions } from "@tiptap/extension-link";

export type CorcaDocumentLinkNavigator = (docId: string) => void;

type CorcaLinkOptions = LinkOptions & {
  doc: DocumentModel;
  onOpenCorcaDocumentLink?: CorcaDocumentLinkNavigator;
};

export const CORCA_DOCUMENT_LINK_CLASS =
  "inline rounded bg-gray-100 px-1 py-0.5 text-gray-600 no-underline hover:bg-blue-100 hover:text-node-active dark:bg-gray-900 dark:text-gray-400 hover:dark:bg-blue-900";

function isCorcaHostname(hostname: string): boolean {
  if (hostname === "corca.app" || hostname.endsWith(".corca.app")) {
    return true;
  }

  try {
    return hostname === new URL(getUrlOrigin()).hostname;
  } catch (_err) {
    return false;
  }
}

export function getCorcaDocumentLinkDocId(
  href?: string | null,
  baseUrl = getUrlOrigin(),
): string | undefined {
  if (!href?.trim()) {
    return undefined;
  }

  let url: URL;

  try {
    url = new URL(href, baseUrl);
  } catch (_err) {
    return undefined;
  }

  if (!isCorcaHostname(url.hostname)) {
    return undefined;
  }

  const [, route, encodedDocId] = url.pathname.split("/");
  if (route !== "doc" || !encodedDocId) {
    return undefined;
  }

  try {
    return decodeURIComponent(encodedDocId);
  } catch (_err) {
    return undefined;
  }
}

function getClickedCorcaDocumentId(target: EventTarget | null): string | undefined {
  if (!(target instanceof Element)) {
    return undefined;
  }

  const anchor = target.closest("a[href]");
  if (!(anchor instanceof HTMLAnchorElement)) {
    return undefined;
  }

  return getCorcaDocumentLinkDocId(anchor.getAttribute("href") ?? anchor.href);
}

function getStringAttribute(attributes: Record<string, unknown>, name: string): string | null {
  const value = attributes[name];
  return typeof value === "string" && value.trim() ? value : null;
}

function handleClick(event: MouseEvent, onOpenCorcaDocumentLink?: CorcaDocumentLinkNavigator) {
  if (!(event instanceof MouseEvent)) return false;
  const modifiedClick = event.button !== 0 || hasClickModifier(event);
  if (!onOpenCorcaDocumentLink || event.defaultPrevented || modifiedClick) {
    return false;
  }

  const docId = getClickedCorcaDocumentId(event.target);
  if (!docId) {
    return false;
  }

  event.preventDefault();
  if (event.type === "click") {
    onOpenCorcaDocumentLink(docId);
  }
  return true;
}

export const Link = Base.extend<CorcaLinkOptions>({
  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() ?? [];

    return [
      new Plugin({
        key: new PluginKey("handleCorcaDocumentLinkClick"),
        props: {
          handleClick: (_view, _pos, event) => {
            return handleClick(event, this.options.onOpenCorcaDocumentLink);
          },
          handleDOMEvents: {
            // props.handleClick is driven by mouseup, so it can't cancel default browser navigation
            click: (_view, event) => {
              return handleClick(event, this.options.onOpenCorcaDocumentLink);
            },
          },
        },
      }),
      ...parentPlugins,
    ];
  },

  addKeyboardShortcuts() {
    return {
      "Mod-k": ({ editor }) => {
        const { selection } = editor.state;

        if (!selection.empty) {
          this.options.doc.editor.setIsLinkEditMode(true, { focusOnRender: true });
        }
        return true;
      },
    };
  },

  renderHTML(props) {
    const rendered = this.parent?.(props) ?? ["a", props.HTMLAttributes, 0];
    const attributes = Array.isArray(rendered)
      ? (rendered[1] as Record<string, unknown>)
      : undefined;

    if (attributes && getCorcaDocumentLinkDocId(getStringAttribute(attributes, "href"))) {
      attributes.class = CORCA_DOCUMENT_LINK_CLASS;
    }

    return rendered;
  },
});
